import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';

export interface Sale {
  id?: string;
  clientId: string;
  clientName: string;
  quantity: number;
  pricePerLiter: number;
  total: number;
  date: string;
}

// Type for creating a new sale without total (it will be calculated)
export type NewSale = Omit<Sale, 'id' | 'total'>;

class SaleService {
  private readonly COLLECTION_NAME = 'sales';

  private checkAuth() {
    const auth = getAuth();
    console.log('Checking authentication state...');
    console.log('Current user:', auth.currentUser);
    
    if (!auth.currentUser) {
      console.error('No authenticated user found');
      throw new Error('Please log in to continue');
    }
    
    console.log('User is authenticated:', {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email
    });
  }

  // Helper function to ensure exact numbers
  private ensureExactNumber(num: number): number {
    // Convert to string to check if it's a whole number
    const numStr = num.toString();
    
    // If the number has no decimal point or only zeros after decimal point
    if (numStr.indexOf('.') === -1 || /\.0+$/.test(numStr)) {
      return parseInt(numStr, 10);
    }
    
    // For decimal numbers, keep exactly 2 decimal places
    return parseFloat(num.toFixed(2));
  }

  // Add a new sale
  async addSale(sale: NewSale): Promise<Sale> {
    try {
      this.checkAuth();
      
      // Ensure exact numbers for both quantity and price
      const quantity = this.ensureExactNumber(sale.quantity);
      const pricePerLiter = this.ensureExactNumber(sale.pricePerLiter);
      const total = this.ensureExactNumber(quantity * pricePerLiter);

      const saleData: Sale = {
        ...sale,
        quantity,
        pricePerLiter,
        total,
        date: Timestamp.fromDate(new Date()).toDate().toISOString()
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), saleData);
      return { ...saleData, id: docRef.id };
    } catch (error) {
      console.error('Error adding sale:', error);
      throw error;
    }
  }

  // Get all sales
  async getSales(): Promise<Sale[]> {
    try {
      this.checkAuth();
      console.log('Fetching all sales...');
      
      const q = query(collection(db, this.COLLECTION_NAME), orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      console.log('Raw query snapshot:', querySnapshot);
      console.log('Number of documents:', querySnapshot.size);
      
      const sales = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Document data:', { id: doc.id, ...data });
        return {
          id: doc.id,
          ...data,
          date: data.date instanceof Timestamp ? data.date.toDate().toISOString() : data.date
        } as Sale;
      });
      
      console.log('Processed sales:', sales);
      return sales;
    } catch (error) {
      console.error('Error getting sales:', error);
      throw error;
    }
  }

  // Get sales by date
  async getSalesByDate(date: string): Promise<Sale[]> {
    try {
      this.checkAuth();
      
      // Create start and end of the day for the given date
      const startDate = new Date(date);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(date);
      endDate.setHours(23, 59, 59, 999);

      // Convert dates to Firestore Timestamps
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);

      console.log('Querying sales between:', {
        startDate: startTimestamp.toDate().toISOString(),
        endDate: endTimestamp.toDate().toISOString()
      });

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const sales = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date instanceof Timestamp ? data.date.toDate().toISOString() : data.date
        } as Sale;
      });
      
      console.log('Found sales:', sales);
      return sales;
    } catch (error) {
      console.error('Error getting sales by date:', error);
      throw error;
    }
  }

  // Get sales by date range
  async getSalesByDateRange(startDate: string, endDate: string): Promise<Sale[]> {
    try {
      this.checkAuth();
      
      // Convert dates to Firestore Timestamps
      const startTimestamp = Timestamp.fromDate(new Date(startDate));
      const endTimestamp = Timestamp.fromDate(new Date(endDate));

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Sale));
    } catch (error) {
      console.error('Error getting sales by date range:', error);
      throw error;
    }
  }

  // Update a sale
  async updateSale(id: string, sale: Partial<NewSale>): Promise<void> {
    try {
      this.checkAuth();
      
      // If quantity or price is being updated, recalculate total
      if (sale.quantity !== undefined || sale.pricePerLiter !== undefined) {
        const currentSale = await this.getSaleById(id);
        if (currentSale) {
          const quantity = this.ensureExactNumber(sale.quantity ?? currentSale.quantity);
          const pricePerLiter = this.ensureExactNumber(sale.pricePerLiter ?? currentSale.pricePerLiter);
          const total = this.ensureExactNumber(quantity * pricePerLiter);
          
          const saleRef = doc(db, this.COLLECTION_NAME, id);
          await updateDoc(saleRef, {
            ...sale,
            quantity,
            pricePerLiter,
            total
          });
          return;
        }
      }

      const saleRef = doc(db, this.COLLECTION_NAME, id);
      await updateDoc(saleRef, sale);
    } catch (error) {
      console.error('Error updating sale:', error);
      throw error;
    }
  }

  // Delete a sale
  async deleteSale(id: string): Promise<void> {
    try {
      this.checkAuth();
      const saleRef = doc(db, this.COLLECTION_NAME, id);
      await deleteDoc(saleRef);
    } catch (error) {
      console.error('Error deleting sale:', error);
      throw error;
    }
  }

  async getSaleById(id: string): Promise<Sale | null> {
    try {
      const saleRef = doc(db, this.COLLECTION_NAME, id);
      const saleDoc = await getDoc(saleRef);
      
      if (saleDoc.exists()) {
        return {
          id: saleDoc.id,
          ...saleDoc.data()
        } as Sale;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting sale by ID:', error);
      throw error;
    }
  }
}

export const saleService = new SaleService(); 